import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { SeasonService } from 'src/app/services/season.service';
import { SeasonPassService } from 'src/app/services/season-pass.service';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { ProductService } from 'src/app/services/product.service';

/**
 * Opciones del filtro de estado. `not_cancelled` no es un estado del backend,
 * es la vista por defecto: esconde los intentos de pago descartados (cada
 * reintento del abonado cancela el anterior, ver
 * SeasonPassRenewalRepository::settlePendingBeforeNewPayment), que son filas
 * de la tabla pero no pedidos que el club tenga que mirar.
 */
export const ORDER_STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'not_cancelled', label: 'Sin cancelados' },
  { value: '', label: 'Todos los estados' },
  { value: 'paid', label: 'Pagados' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'error', label: 'Con error' },
  { value: 'refused', label: 'Denegados' },
  { value: 'refund', label: 'Devueltos' },
];

const DEFAULT_STATUS_FILTER = 'not_cancelled';

/**
 * Listado de pedidos de abono de temporada: cada fila es una renovación
 * (SeasonPassRenewal), es decir un intento de cobro del abono, pagado o no.
 */
@Component({
  selector: 'app-sports-passes-orders-list',
  templateUrl: './orders-list.component.html',
  styleUrls: ['./orders-list.component.scss']
})
export class SeasonPassOrdersListComponent implements OnInit {

  business: any;
  orders: any[] = [];
  seasons: any[] = [];
  products: any[] = [];
  loaded: boolean = false;
  loadingPage: boolean = true;

  searchText: string = '';
  seasonFilter: number | '' = '';
  seasonDropdownOpen: boolean = false;

  // El desplegable de producto se acota a la temporada elegida, para no
  // ofrecer productos que no van a devolver ningún pedido.
  productFilter: number | '' = '';
  productDropdownOpen: boolean = false;

  statusFilter: string = DEFAULT_STATUS_FILTER;
  statusDropdownOpen: boolean = false;
  readonly statusOptions = ORDER_STATUS_FILTERS;

  // Scroll infinito: se piden más páginas al llegar al final de la tabla.
  page: number = 1;
  finishScroll: boolean = false;
  loadingMore: boolean = false;
  @ViewChild('containerScroll') containerScroll!: ElementRef;

  // Descarta respuestas de una carga anterior: al cambiar un filtro se pide la
  // página 1 de otra cosa y la petición en vuelo ya no vale.
  private loadGeneration: number = 0;

  // Totales de TODOS los pedidos que casan con los filtros, no de la página
  // servida: los calcula el backend (`summary`) porque el listado llega
  // paginado y sumar aquí solo contaría lo visible.
  totalOrders: number = 0;
  totalCollected: number = 0;
  paidCount: number = 0;
  pendingCount: number = 0;

  private searchTerms = new Subject<string>();

  displayedColumns: string[] = ['num_ticket', 'name', 'season', 'product', 'created_at', 'total', 'status'];
  dataSource: MatTableDataSource<any> = new MatTableDataSource<any>();

  @ViewChild(MatSort) sort: MatSort = new MatSort();

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-group')) {
      this.seasonDropdownOpen = false;
      this.productDropdownOpen = false;
      this.statusDropdownOpen = false;
    }
  }

  constructor(
    private router: Router,
    private seasonService: SeasonService,
    private seasonPassService: SeasonPassService,
    private apiBusiness: ApiBusinessService,
    private productService: ProductService,
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit() {
    this.apiBusiness.changed.subscribe({
      next: (value) => {
        this.business = JSON.parse(localStorage.getItem('business') || '{}');
        if (value) {
          this.loadSeasons();
          this.loadProducts();
          this.loadOrders();
        }
      }
    });

    this.searchTerms.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchText = term;
      this.loadOrders();
    });
  }

  loadSeasons() {
    const companyId = this.business?.id;
    if (!companyId) return;
    this.seasonService.getSeasonsByCompany(companyId).subscribe({
      next: (res) => { this.seasons = res.data || []; }
    });
  }

  /**
   * Productos para el desplegable, acotados a la temporada filtrada si hay una.
   * Se piden con `getProducts` y no con `getProductsBySeason` porque este último
   * devuelve solo los activos, y un producto retirado puede seguir teniendo
   * pedidos antiguos que hay que poder filtrar.
   */
  loadProducts() {
    const companyId = this.business?.id;
    if (!companyId) return;
    const filters = this.seasonFilter ? { id_season: +this.seasonFilter } : undefined;
    this.productService.getProducts(companyId, filters).subscribe({
      next: (res) => { this.products = res.data || []; },
      error: () => { this.products = []; }
    });
  }

  /**
   * `append` distingue la carga de una página más (scroll) de una carga nueva
   * (primera vez o cambio de filtro), que reinicia el listado.
   */
  loadOrders(append: boolean = false) {
    const companyId = this.business?.id;

    if (!append) {
      this.page = 1;
      this.finishScroll = false;
      this.loadingPage = true;
      this.loadGeneration++;
    }

    if (!companyId) {
      this.setOrders(null, append);
      return;
    }

    const filters: any = {};
    if (this.searchText) filters.search = this.searchText;
    if (this.seasonFilter) filters.id_season = this.seasonFilter;
    if (this.productFilter) filters.id_product = this.productFilter;

    if (this.statusFilter === DEFAULT_STATUS_FILTER) {
      filters.exclude_status = 'cancelled';
    } else if (this.statusFilter) {
      filters.status = this.statusFilter;
    }

    const generation = this.loadGeneration;

    this.seasonPassService.getRenewalsByCompany(companyId, filters, this.page).subscribe({
      next: (res) => {
        if (generation !== this.loadGeneration) return;
        this.setOrders(res?.data, append);
      },
      error: () => {
        if (generation !== this.loadGeneration) return;
        this.setOrders(null, append);
      }
    });
  }

  /**
   * Vuelca la página recibida. Un error corta el scroll (`finishScroll`) para no
   * quedarse reintentando la misma página cada vez que el usuario mueve la rueda.
   */
  private setOrders(payload: any, append: boolean) {
    // Un backend que todavía no pagina devuelve el array plano en `data` (pasa
    // mientras la API paginada no esté desplegada en el entorno): se trata como
    // página única, para no quedarse pidiendo páginas que no existen.
    const paginated = !!payload && !Array.isArray(payload);
    const rows = (paginated ? payload.data : payload) || [];
    this.orders = append ? this.orders.concat(rows) : rows;
    this.finishScroll = !paginated || payload.current_page >= payload.last_page;

    if (payload?.summary) {
      this.totalOrders = payload.summary.total || 0;
      this.totalCollected = +payload.summary.total_collected || 0;
      this.paidCount = payload.summary.paid_count || 0;
      this.pendingCount = payload.summary.pending_count || 0;
    } else if (!append) {
      // Sin `summary` —API sin paginar todavía, o error— se resume lo cargado,
      // que es lo que hacía esta pantalla antes de paginar.
      const paid = this.orders.filter(o => o.status?.short_name === 'paid');
      this.totalOrders = this.orders.length;
      this.paidCount = paid.length;
      this.pendingCount = this.orders.filter(o => o.status?.short_name === 'pending').length;
      this.totalCollected = paid.reduce((sum, o) => sum + (+o.total || 0), 0);
    }

    this.dataSource = new MatTableDataSource(this.orders);
    this.dataSource.sort = this.sort;
    this.loaded = true;
    this.loadingPage = false;
    this.loadingMore = false;
  }

  @HostListener('window:scroll', ['$event'])
  @HostListener('scroll', ['$event'])
  isScrolledInfinityView(event: any) {
    if (this.loadingPage || this.loadingMore || this.finishScroll || !this.containerScroll) return;

    const element = this.containerScroll.nativeElement.getBoundingClientRect();
    const bottomShown = element.bottom <= window.innerHeight
      || event?.target?.offsetHeight + event?.target?.scrollTop >= event?.target?.scrollHeight;

    if (bottomShown) {
      this.page++;
      this.loadingMore = true;
      this.loadOrders(true);
    }
  }

  onSearch(value: string) {
    this.searchTerms.next(value);
  }

  /**
   * Solo un desplegable abierto a la vez: el clic en una píldora no llega al
   * `document:click` que cierra el otro, porque también está dentro de un
   * `.filter-group`.
   */
  toggleSeasonDropdown() {
    this.seasonDropdownOpen = !this.seasonDropdownOpen;
    this.productDropdownOpen = false;
    this.statusDropdownOpen = false;
  }

  toggleProductDropdown() {
    this.productDropdownOpen = !this.productDropdownOpen;
    this.seasonDropdownOpen = false;
    this.statusDropdownOpen = false;
  }

  toggleStatusDropdown() {
    this.statusDropdownOpen = !this.statusDropdownOpen;
    this.seasonDropdownOpen = false;
    this.productDropdownOpen = false;
  }

  selectStatusFilter(status: string) {
    this.statusFilter = status;
    this.statusDropdownOpen = false;
    this.loadOrders();
  }

  get statusFilterLabel(): string {
    return this.statusOptions.find(o => o.value === this.statusFilter)?.label || 'Estado';
  }

  get isDefaultStatusFilter(): boolean {
    return this.statusFilter === DEFAULT_STATUS_FILTER;
  }

  /** Si el filtro de estado no es el de por defecto, cuenta como filtro puesto. */
  get hasActiveFilters(): boolean {
    return !!this.searchText || !!this.seasonFilter || !!this.productFilter || !this.isDefaultStatusFilter;
  }

  /**
   * Al cambiar de temporada se limpia el producto y se recarga su desplegable:
   * el producto elegido puede no existir en la nueva temporada, y esperar a la
   * respuesta para decidirlo dejaría el filtro dependiendo del orden de las
   * peticiones.
   */
  selectSeasonFilter(seasonId: number | '') {
    this.seasonFilter = seasonId;
    this.seasonDropdownOpen = false;
    this.productFilter = '';
    this.loadProducts();
    this.loadOrders();
  }

  get seasonFilterLabel(): string {
    if (this.seasonFilter === '') return 'Temporada';
    const season = this.seasons.find(s => s.id === this.seasonFilter);
    return season ? season.name : 'Temporada';
  }

  selectProductFilter(productId: number | '') {
    this.productFilter = productId;
    this.productDropdownOpen = false;
    this.loadOrders();
  }

  get productFilterLabel(): string {
    if (this.productFilter === '') return 'Producto';
    const product = this.products.find(p => p.id === this.productFilter);
    return product ? product.name : 'Producto';
  }

  clearFilters() {
    this.searchText = '';
    this.seasonFilter = '';
    this.productFilter = '';
    this.statusFilter = DEFAULT_STATUS_FILTER;
    this.loadProducts();
    this.loadOrders();
  }

  openDetail(order: any) {
    this.router.navigate(['/panel-control/sports-passes/orders', order.id]);
  }

  getStatusBadgeClass(order: any): string {
    return getOrderStatusBadgeClass(order?.status?.short_name);
  }

  getStatusIcon(order: any): string {
    return getOrderStatusIcon(order?.status?.short_name);
  }
}

/**
 * Colores y iconos de estado de pedido, compartidos entre el listado y el
 * detalle para que un mismo pedido no se vea de dos maneras distintas.
 * `short_name` son los de config/statuses.php en el backend.
 */
export function getOrderStatusBadgeClass(shortName: string | undefined): string {
  if (shortName === 'paid') return 'status-badge--active';
  if (shortName === 'pending' || shortName === 'processing') return 'status-badge--pending';
  if (shortName === 'error' || shortName === 'refused' || shortName === 'cancelled') return 'status-badge--ended';
  if (shortName === 'refund' || shortName === 'partial_refund' || shortName === 'pending_refund') return 'status-badge--refund';
  return 'status-badge--inactive';
}

export function getOrderStatusIcon(shortName: string | undefined): string {
  if (shortName === 'paid') return 'fa-circle-check';
  if (shortName === 'pending' || shortName === 'processing') return 'fa-circle-pause';
  if (shortName === 'error' || shortName === 'refused' || shortName === 'cancelled') return 'fa-circle-xmark';
  if (shortName === 'refund' || shortName === 'partial_refund' || shortName === 'pending_refund') return 'fa-rotate-left';
  return 'fa-circle-minus';
}
